import React, { useState } from "react";
import { resetPassword } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Milk, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { useT } from "@/i18n/I18nContext";
import { toast } from "sonner";

export default function ResetPassword() {
  const { t } = useT();
  const token = new URLSearchParams(window.location.search).get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [status, setStatus] = useState(token ? "form" : "error");
  const [message, setMessage] = useState(token ? "" : t("reset.missing"));

  const submit = async () => {
    if (pw.length < 6) { toast.error(t("reset.tooShort")); return; }
    if (pw !== pw2) { toast.error(t("reset.mismatch")); return; }
    setStatus("loading");
    try {
      await resetPassword(token, pw);
      setStatus("success");
    } catch (err) {
      const d = err.response?.data?.detail;
      setMessage(typeof d === "string" ? d : t("reset.failed"));
      setStatus("error");
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center header-band px-4 py-10">
      <div className="absolute right-4 top-4"><LanguageSwitcher /></div>
      <div className="w-full max-w-md">
        <div className="mb-6 flex flex-col items-center text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[hsl(var(--brand))] text-white shadow-lg">
            <Milk className="h-7 w-7" />
          </div>
          <h1 className="mt-3 text-xl font-semibold">{t("reset.title")}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t("reset.subtitle")}</p>
        </div>
        <Card>
          <CardContent className="p-8">
            {status === "form" && (
              <div className="space-y-4" data-testid="reset-form">
                <div className="space-y-1.5">
                  <Label>{t("reset.newPassword")}</Label>
                  <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-password" />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("reset.confirmPassword")}</Label>
                  <Input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} data-testid="reset-password-confirm" />
                </div>
                <Button className="w-full" onClick={submit} data-testid="reset-submit">{t("reset.reset")}</Button>
              </div>
            )}
            {status === "loading" && (
              <div className="flex flex-col items-center text-center" data-testid="reset-loading">
                <Loader2 className="h-10 w-10 animate-spin text-[hsl(var(--brand))]" />
              </div>
            )}
            {status === "success" && (
              <div className="flex flex-col items-center text-center" data-testid="reset-success">
                <CheckCircle2 className="h-12 w-12 text-[hsl(var(--brand))]" />
                <h2 className="mt-4 text-lg font-semibold">{t("reset.successTitle")}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{t("reset.successBody")}</p>
                <Button className="mt-5 w-full" onClick={() => { window.location.href = "/login"; }} data-testid="reset-login-button">
                  {t("reset.goToLogin")}
                </Button>
              </div>
            )}
            {status === "error" && (
              <div className="flex flex-col items-center text-center" data-testid="reset-error">
                <XCircle className="h-12 w-12 text-[hsl(var(--destructive))]" />
                <h2 className="mt-4 text-lg font-semibold">{t("reset.errorTitle")}</h2>
                <p className="mt-2 text-sm text-muted-foreground">{message || t("reset.failed")}</p>
                <Button variant="outline" className="mt-5 w-full" onClick={() => { window.location.href = "/login"; }} data-testid="reset-login-button">
                  {t("reset.goToLogin")}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
